import { useMutation, useQueryClient } from '@tanstack/react-query';
import { commentApi, translationApi, systemApi } from '../services/api';
import { commentCountsKey } from './queries';

export function useCreateComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { canonicalBookId: number; chapterNumber: number; verseNumber: number; author: string; text: string }) =>
      commentApi.create(params.canonicalBookId, params.chapterNumber, params.verseNumber, params.author, params.text),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['comments', variables.canonicalBookId, variables.chapterNumber, variables.verseNumber] });
      // The verse may have just gained its first comment, which is what the marker in the verse list reads.
      queryClient.invalidateQueries({ queryKey: commentCountsKey(variables.canonicalBookId, variables.chapterNumber) });
    },
  });
}

export function useUpdateComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: number; text: string; canonicalBookId: number; chapterNumber: number; verseNumber: number }) =>
      commentApi.update(params.id, params.text),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['comments', variables.canonicalBookId, variables.chapterNumber, variables.verseNumber] });
    },
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: number; canonicalBookId: number; chapterNumber: number; verseNumber: number }) =>
      commentApi.delete(params.id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['comments', variables.canonicalBookId, variables.chapterNumber, variables.verseNumber] });
      // That may have been the verse's last comment, which drops the marker in the verse list.
      queryClient.invalidateQueries({ queryKey: commentCountsKey(variables.canonicalBookId, variables.chapterNumber) });
    },
  });
}

export function useDeleteBible() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bibleId: number) => translationApi.delete(bibleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bibles'] });
      queryClient.invalidateQueries({ queryKey: ['verses'] });
      queryClient.invalidateQueries({ queryKey: ['chapters'] });
    },
  });
}

export function useImportBible() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { file: File; authHeaders: Record<string, string> }) =>
      systemApi.importBible(params.file, params.authHeaders),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bibles'] });
      queryClient.invalidateQueries({ queryKey: ['verses'] });
      queryClient.invalidateQueries({ queryKey: ['chapters'] });
    },
  });
}
